import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <Link to="/login" data-testid="forgot-back-link" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#990000] mb-6">
                    <ArrowLeft size={16} /> Back to sign in
                </Link>
                <div className="bg-white border border-stone-200 rounded-xl p-8">
                    {submitted ? (
                        <div className="text-center" data-testid="forgot-password-success">
                            <div className="w-12 h-12 rounded-full bg-[#FFF4CC] border border-[#FFE680] flex items-center justify-center mx-auto mb-4">
                                <CheckCircle size={22} className="text-[#7A0000]" weight="fill" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0A0A0A] mb-2">Check your inbox</h1>
                            <p className="text-stone-500 text-sm">
                                If an account exists for <span className="font-medium text-stone-700">{email}</span>, we've sent a password reset link.
                                It expires in 1 hour.
                            </p>
                            <p className="text-xs text-stone-400 mt-4">
                                (Dev mode: check the backend console logs for the reset link.)
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">Recover</div>
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-2">
                                Forgot your password?
                            </h1>
                            <p className="text-stone-500 mb-6 text-sm">
                                Enter the email tied to your TrojanTrails account and we'll send a reset link.
                            </p>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        data-testid="forgot-email-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@usc.edu"
                                        className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]"
                                        required
                                    />
                                </div>
                                {error && (
                                    <div className="text-sm text-[#990000] bg-[#990000]/5 border border-[#990000]/20 rounded-lg px-3 py-2">{error}</div>
                                )}
                                <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                                        className="w-full h-11 bg-[#990000] hover:bg-[#7A0000] text-white font-medium">
                                    {loading ? "Sending…" : "Send reset link"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
